import { useEffect, useState } from "react";
import type { FormEvent, MouseEvent as ReactMouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { mainNav, site } from "@/mocks/site";

interface SiteHeaderProps {
  brandAsHeading?: boolean;
}

function scrollToHash(hash: string) {
  const id = hash.replace("#", "");
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }
  return false;
}

export default function SiteHeader({ brandAsHeading = false }: SiteHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNavClick = (to: string) => (event: ReactMouseEvent) => {
    if (to.startsWith("/#")) {
      if (location.pathname === "/") {
        event.preventDefault();
        scrollToHash(to.slice(1));
      } else {
        event.preventDefault();
        navigate("/");
        window.setTimeout(() => scrollToHash(to.slice(1)), 120);
      }
    }
    setMenuOpen(false);
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}#latest` : "/");
    window.setTimeout(() => scrollToHash("#latest"), 160);
  };

  const BrandTag = brandAsHeading ? "h1" : "div";

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="bg-foreground-950 text-background-50">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-4 px-4 py-1.5 text-xs md:px-6">
          <p className="flex items-center gap-2 whitespace-nowrap">
            <i className="ri-flashlight-fill text-accent-400" aria-hidden="true"></i>
            <span className="text-background-200">今日快讯：</span>
            <span className="text-background-50">电影节红毯复盘 · 慢综艺改版 · 音乐节音响升级</span>
          </p>
          <p className="hidden whitespace-nowrap text-background-300 sm:block">
            {new Date().getFullYear()} 年 9 月 · 娱乐资讯门户
          </p>
        </div>
      </div>

      <div
        className={`border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-6 px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${site.name} 首页`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-emotion-happy-fill text-xl text-background-50" aria-hidden="true"></i>
            </span>
            <BrandTag className="leading-none">
              <span className="block font-heading text-2xl font-bold tracking-wide text-foreground-950">
                {site.name}
              </span>
              <span className="mt-0.5 block text-[10px] font-medium uppercase tracking-[0.28em] text-primary-700">
                {site.nameEn}
              </span>
            </BrandTag>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {mainNav.map((item) => {
              const active = item.to === location.pathname;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={handleNavClick(item.to)}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              role="search"
              className="hidden items-center gap-2 rounded-full border border-background-300/70 bg-background-100 px-3 py-1.5 md:flex"
            >
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                name="header-search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索明星、影视、综艺…"
                aria-label="站内搜索"
                className="w-40 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 lg:w-52"
              />
            </form>

            <Link
              to="/contact"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-block"
            >
              提供线索
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
              aria-expanded={menuOpen}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <nav className="mx-auto flex w-full max-w-[1400px] flex-col px-4 py-3 md:px-6" aria-label="移动端导航">
              {mainNav.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={handleNavClick(item.to)}
                  className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="mt-2 rounded-md bg-primary-500 px-3 py-3 text-center text-base font-medium text-background-50"
              >
                提供线索
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}